import { useCallback, useMemo } from "react";
import {
	BaseListContainer,
	BaseListFooterContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
	CommonListProps,
} from "./OptionListsComponents";
import { CoreList, CoreListRow, CorePagination } from "../../../core";
import { useFocusElementOnMount } from "../../../hooks";
import i18nPackage from "../../../i18n/i18n";
import { EditableListToolbar, EditableListCell, EditableListRemoveButton } from "./EditableList";
import useEditableList from "../hooks/useEditableList";
import KeyboardBlockIcon from "../../images/configuration/keyboard-block-list.svg?react";

const MODIFIER_KEYS = ["control", "ctrl", "meta", "shift", "alt", "command", "cmd", "super", "option", "altgr"];
const PAGE_SIZE = 4;

type Shortcut = {
	shortcut: string;
};

type KeyboardBlockListProps = {
	initialBlockList: string[];
	onUpdate: (blockList: string[]) => void;
} & CommonListProps;

export default function KeyboardBlockList({
	initialBlockList,
	onUpdate,
	focusOnMount,
	onFocus,
}: KeyboardBlockListProps) {
	const { ref } = useFocusElementOnMount<HTMLDivElement>(focusOnMount, onFocus);

	const memoizedMakeItem = useCallback((val: string) => ({ shortcut: val }), []);
	const memoizedOnUpdate = useCallback((items: Shortcut[]) => onUpdate(items.map((i) => i.shortcut)), [onUpdate]);

	const initialItems = useMemo(() => initialBlockList.map((shortcut) => ({ shortcut })), [initialBlockList]);

	const { pagination, rows, searchState, tableActions, tableState } = useEditableList<Shortcut>({
		initialItems,
		onUpdate: memoizedOnUpdate,
		pageSize: PAGE_SIZE,
		makeItem: memoizedMakeItem,
	});

	const { baseTableRows, tableRows } = rows;
	const { page, setPage, totalPages } = pagination;
	const { searchQuery, setSearchQuery } = searchState;
	const { selectedCount } = tableState;
	const { handleAdd, handleDeleteOne } = tableActions;

	const updateShortcut = (event: React.KeyboardEvent<HTMLElement>) => {
		let { key } = event;
		const keys: string[] = [];
		if (event.ctrlKey) {
			keys.push("Ctrl");
		}
		if (event.metaKey) {
			keys.push("Meta");
		}
		if (event.shiftKey) {
			keys.push("Shift");
		}
		if (event.altKey) {
			keys.push("Alt");
		}
		if (!MODIFIER_KEYS.includes(key.toLowerCase())) {
			if (key === " ") {
				key = "Space";
			}
			keys.push(key.length > 1 ? key : key.toUpperCase());
		}
		setSearchQuery(keys.join("+"));
	};

	const validate = useCallback(
		(shortcut: string) => {
			const trimmed = shortcut.trim();
			const existing = baseTableRows.map((x) => x.shortcut);

			//  Check for duplicates
			const isDuplicate = existing.some((e) => e.toLowerCase() === trimmed.toLowerCase());

			// Return true ONLY if all technical checks pass AND it's not a duplicate
			return !isDuplicate && trimmed.length > 0;
		},
		[baseTableRows],
	);

	return (
		<BaseListContainer ref={ref}>
			<BaseListHeaderContainer>
				<BaseListInfoArea
					title={i18nPackage.t("editQuizPage.keyboardBlockList.title")}
					description={i18nPackage.t("editQuizPage.keyboardBlockList.description")}
					icon={<KeyboardBlockIcon />}
					slotProps={{
						...(selectedCount !== 0 && {
							badge: {
								text: i18nPackage.t("editQuizPage.keyboardBlockList.badgeText", { count: selectedCount }),
							},
						}),
					}}
				/>
				<EditableListToolbar
					searchQuery={searchQuery}
					onSearchChange={() => {}}
					onKeyDown={updateShortcut}
					handleAdd={handleAdd}
					canAddOrEdit={validate}
					placeholder={i18nPackage.t("editQuizPage.keyboardBlockList.placeholder")}
					textFieldLabel={i18nPackage.t("editQuizPage.keyboardBlockList.textFieldLabel")}
				/>
			</BaseListHeaderContainer>
			<BaseListFooterContainer>
				{tableRows.length === 0 ? null : (
					<CoreList maxHeight={350}>
						{tableRows.map((row) => (
							<CoreListRow
								key={row.id}
								label={<EditableListCell<Shortcut> row={row} dataKey="shortcut" canAddOrEdit={validate} />}
								action={
									!row.editing && (
										<EditableListRemoveButton
											onClick={() => handleDeleteOne(row.id)}
											ariaLabel={i18nPackage.t("editQuizPage.keyboardBlockList.deleteAriaLabel", {
												name: row.shortcut,
											})}
										/>
									)
								}
							/>
						))}
					</CoreList>
				)}
				{baseTableRows.length > PAGE_SIZE && totalPages > 1 && (
					<CorePagination page={page} count={totalPages} onChange={(_, p) => setPage(p)} />
				)}
			</BaseListFooterContainer>
		</BaseListContainer>
	);
}
